/**
 * API权限配置管理
 * 提供后端权限模式和本地路由模式的切换功能
 */

// API 权限配置项
export interface ApiPermissionConfig {
  /** 是否启用后端权限模式 */
  enabled: boolean;
  /** 是否使用后端权限模式 (与 enabled 配合使用) */
  useBackendPermission?: boolean;
  /** 开发环境模式 */
  devMode?: {
    enabled?: boolean;
    showDetailedLogs?: boolean;
    simulatePermissionDenied?: boolean;
  };
  /** 生产环境模式 */
  prodMode?: {
    enabled?: boolean;
    strictMode?: boolean;
  };
  /** API 白名单 */
  whitelist?: string[];
}

// API 权限切换配置（用于组件和 hook）
export type ApiPermissionSwitchConfig = ApiPermissionConfig;

// 默认配置
export const DEFAULT_API_PERMISSION_CONFIG: ApiPermissionConfig = {
  enabled: true, // 默认启用后端权限模式
};

/**
 * API权限配置管理类
 */
export class ApiPermissionConfigManager {
  private static instance: ApiPermissionConfigManager;
  private config: ApiPermissionConfig;
  private localStorageKey = 'api_permission_mode';

  private constructor() {
    this.config = this.loadConfig();
  }

  public static getInstance(): ApiPermissionConfigManager {
    if (!ApiPermissionConfigManager.instance) {
      ApiPermissionConfigManager.instance = new ApiPermissionConfigManager();
    }
    return ApiPermissionConfigManager.instance;
  }

  /**
   * 加载配置（优先从 localStorage，其次使用默认配置）
   */
  private loadConfig(): ApiPermissionConfig {
    try {
      const savedConfig = localStorage.getItem(this.localStorageKey);
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig);
        return {
          ...DEFAULT_API_PERMISSION_CONFIG,
          ...parsedConfig,
        };
      }
    } catch (error) {
      console.warn('加载 API权限配置失败，使用默认配置:', error);
    }

    return DEFAULT_API_PERMISSION_CONFIG;
  }

  /**
   * 获取当前生效的配置
   */
  public getConfig(): ApiPermissionConfig {
    return { ...this.config };
  }

  /**
   * 更新配置
   */
  public updateConfig(newConfig: Partial<ApiPermissionConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };

    // 保存到 localStorage
    this.saveConfig();

    // 触发配置变更回调
    this.notifyConfigChange();
  }

  /**
   * 保存配置到 localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(this.config));
    } catch (error) {
      console.error('保存 API权限配置失败:', error);
    }
  }

  /**
   * 重置为默认配置
   */
  public resetToDefault(): void {
    this.config = DEFAULT_API_PERMISSION_CONFIG;
    localStorage.removeItem(this.localStorageKey);
    this.notifyConfigChange();
  }

  /**
   * 检查是否应该执行后端权限检查
   */
  public shouldCheckPermission(): boolean {
    return this.config.enabled;
  }

  /**
   * 配置变更回调函数数组
   */
  private configChangeCallbacks: Array<() => void> = [];

  /**
   * 注册配置变更回调
   */
  public onConfigChange(callback: () => void): void {
    this.configChangeCallbacks.push(callback);
  }

  /**
   * 触发配置变更通知
   */
  private notifyConfigChange(): void {
    this.configChangeCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('配置变更回调执行失败:', error);
      }
    });
  }

  /**
   * 获取配置摘要信息（用于调试）
   */
  public getConfigSummary(): any {
    return {
      enabled: this.config.enabled,
      mode: this.config.enabled ? '后端权限模式' : '本地路由模式',
    };
  }
}

// 导出单例实例
export const apiPermissionConfigManager =
  ApiPermissionConfigManager.getInstance();

// 便捷的 hooks
export const useApiPermissionConfig = () => {
  return apiPermissionConfigManager;
};
